import { useState, useEffect } from 'react';
import { friendlyFileName } from '../constants/borrowerDocumentName';

// Renders a message attachment WITHOUT ever putting an account/staff JWT in the
// URL. It first calls GET /api/messages/attachments/:id/url (header-authed) to
// get a short-lived, attachment-scoped signed token, then loads the file via
// ?t=<token>. Its own component so the effect obeys the rules of hooks when
// rendered inside a message .map(). Works for both borrower and staff callers —
// the backend bakes the principal into the signed token.
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const IMG_RE = /\.(png|jpe?g|gif|webp|heic|heif)$/i;

// 🔑 mintPath (the staff composer, 2026-10-31): where the short-lived link is minted. Default: the borrower/staff route
// above. DoorlyPro passes its own (/api/doorlypro/messages/:id/attachment-url, partner-token authed); the server
// answers with the same attachment token and the url to load.
// 🔑 borrowerView (Hannah's review, 2026-11-02 item 4): the borrower's Messages show the file by its borrower name
// (constants/borrowerDocumentName); staff screens leave it off and keep the original file name (also as the tooltip).
// The original decides image-or-file either way.
export default function MessageAttachment({ msgId, name, token, dark, mintPath = null, borrowerView = false }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token || !msgId) return undefined;
    let alive = true;
    fetch(`${API}${mintPath || `/api/messages/attachments/${msgId}/url`}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        if (d && d.token && mintPath && typeof d.url === 'string' && d.url.startsWith('/api/messages/attachments/')) setUrl(`${API}${d.url}`);
        else if (d && d.token && !mintPath) setUrl(`${API}/api/messages/attachments/${msgId}?t=${encodeURIComponent(d.token)}`);
        else setFailed(true);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [msgId, token, mintPath]);

  const linkColor = dark ? '#dbeafe' : '#3a6ad9';
  const shown = borrowerView ? friendlyFileName(name) : name;
  if (failed) return <div style={{ fontSize: 11, fontStyle: 'italic', opacity: 0.7 }}>Attachment unavailable</div>;
  if (!url) return <div style={{ fontSize: 11, fontStyle: 'italic', opacity: 0.7 }}>Loading attachment…</div>;

  return IMG_RE.test(name || '') ? (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="message-attachment-link" style={{ display: 'block', marginTop: 2 }}>
      <img src={url} alt={shown} style={{ display: 'block', maxWidth: '100%', maxHeight: 240, borderRadius: 8 }} />
      <div style={{ fontSize: 10, marginTop: 3, opacity: 0.85, textDecoration: 'underline', color: linkColor }}>{shown}</div>
    </a>
  ) : (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="message-attachment-link"
       style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: linkColor, textDecoration: 'underline' }}>
      📎 <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={borrowerView ? undefined : name}>{shown}</span>
    </a>
  );
}
